// eslint-disable-next-line
import { injectable } from "inversify";
import { TYPE, rootContainer } from '../container/root';
import { IApiResponse, IHttpService } from "../http-service";
import { IFetchListArgs, IPagedData } from '../../stores/abstract-paged-data-store';
import { createPagedRequestURLSearchParams } from '../helpers';
import { IPlan } from "../../models/Plan";
import { ISbrReceipt, ISbrReceiptRequest } from "../../pages/billing/stores/sbr-receipt-request-store";

export enum EXCESS_USAGE_STATUS {
    PENDING = 'pending',
    NOT_CHARGED = 'not charged',
    FAILED = 'failed',
    CHARGED = 'charged'
}

export interface BilledUser {
    name: string;
    shopify_grandfathered: boolean;
    uninstalled_at: string;
    created_at: string;
    updated_at: string;
}

export interface IExcessUsage {
    id: number;
    month: string;
    rma_count: number;
    charged?: number;
    excess_rmas: number;
    price_per_excess_rma?: number;
    excess_rmas_charge?: number;
    period_start: string;
    period_end: string;
    rma_quota: number;
    billed_user: BilledUser;
    parent_billed_user?: BilledUser;
    plan: IPlan;

    order_tracking_count?: number;
    order_tracking_quota?: number;
    excess_order_tracking_count?: number;
    price_per_excess_order_tracking?: number;
    excess_order_tracking_charge?: number;

    total: number;
    status: string;
    duration_remaining: number | string;
    is_first_month_of_annual_plan: boolean;
}

export interface IParentExcessUsage {
    parent_usage: IExcessUsage
    children_usages?: IExcessUsage[]
}

export interface IFetchTransactionListArgs extends IFetchListArgs {
    search_store_name: string;
}

export interface ISbrReceiptGenerateResponse {
    sbr_receipt: ISbrReceipt;
    encoded_pdf: string;
}

@injectable()
class BillingService {
    private readonly httpService: IHttpService = rootContainer.get<IHttpService>(TYPE.ApiHttp);

    async fetchList(args: IFetchTransactionListArgs): Promise<IPagedData<IParentExcessUsage>> {
        const { page, perPage, sortBy, sortDir, search_store_name } = args;
        const searchParams = createPagedRequestURLSearchParams(page, perPage, sortBy, sortDir);

        if (search_store_name) {
            searchParams.append('store', search_store_name);
        }

        const res = await this.httpService.get<IApiResponse<IPagedData<IParentExcessUsage>>>
            (`/rms-api/billing-history?${searchParams.toString()}`);
        return res.payload;
    }

    async fetchPrevBillingUsage(id: number): Promise<IParentExcessUsage> {
        const res = await this.httpService.get<IApiResponse<IParentExcessUsage>>
            (`/rms-api/billing-history/${id}/previous`);
        return res.payload;
    }

    async generateSbrReceipt(request: ISbrReceiptRequest): Promise<ISbrReceiptGenerateResponse> {
        const res = await this.httpService.post<IApiResponse<ISbrReceiptGenerateResponse>>(`/rms-api/billing-history/generate-sbr-receipt`, request);
        return res.payload;
    }

    async fetchLatestSbrReceipt(): Promise<ISbrReceipt|null> {
        const res = await this.httpService.get<IApiResponse<ISbrReceipt|null>>(`/rms-api/billing-history/latest-sbr-receipt`);
        return res.payload;
    }


}

export default BillingService;
